// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The event log drawer.
 *
 * It exists because this tool's central claim — that your rows never leave the
 * device — is invisible. A running commentary of what actually happened, in
 * order, with the model download named and the row counts stated, is the
 * cheapest way to make the claim checkable rather than merely asserted.
 *
 * It obeys the same four-exit dismissal contract as the modals: an in-drawer
 * close button, a backdrop pointerdown, Escape, and a real touch sequence. On a
 * phone the drawer covers the toggle that opened it, so "tap the toggle again"
 * is not an exit.
 *
 * NOTHING FROM THE CORPUS IS EVER LOGGED. Every message is a count, a stage or a
 * filename. A log line quoting a ticket would put customer text into a DOM node
 * that outlives the run, for no benefit at all.
 */

export type LogLevel = 'info' | 'good' | 'warn' | 'bad';

const MARKS: Record<LogLevel, string> = { info: '·', good: '✓', warn: '!', bad: '✕' };

export class EventLog {
  private readonly root: HTMLElement;
  private readonly list: HTMLElement;
  private readonly toggle: HTMLButtonElement;
  private readonly badge: HTMLElement;
  private unread = 0;

  constructor(mount: HTMLElement, toggle: HTMLButtonElement, badge: HTMLElement) {
    this.toggle = toggle;
    this.badge = badge;
    this.root = document.createElement('div');
    this.root.className = 'drawer';
    this.root.id = 'event-drawer';
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', 'Event log');
    this.root.innerHTML = `
      <div class="drawer-backdrop" data-close="1"></div>
      <aside class="drawer-panel">
        <div class="drawer-head">
          <h2>Event log</h2>
          <div class="drawer-actions">
            <button type="button" class="ghost-button" data-copy="1">Copy</button>
            <button type="button" class="icon-button" data-close="1" aria-label="Close the event log">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>
            </button>
          </div>
        </div>
        <ol class="drawer-list" aria-live="polite"></ol>
        <p class="drawer-foot">Every line is a count, a stage or a filename. None of your rows appear here, or anywhere else outside this tab.</p>
      </aside>`;
    mount.appendChild(this.root);
    this.list = this.root.querySelector('.drawer-list') as HTMLElement;

    this.root.addEventListener('pointerdown', (event) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-copy]')) {
        event.preventDefault();
        void this.copy();
        return;
      }
      const panel = this.root.querySelector('.drawer-panel') as HTMLElement;
      if (target.closest('[data-close]') || !panel.contains(target)) {
        event.preventDefault();
        this.close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.root.hidden) {
        event.preventDefault();
        this.close();
      }
    });

    toggle.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      this.root.hidden ? this.open() : this.close();
    });
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(): void {
    this.root.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
    this.unread = 0;
    this.renderBadge();
    (this.root.querySelector('.icon-button') as HTMLElement | null)?.focus();
  }

  close(): void {
    this.root.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.focus();
  }

  add(level: LogLevel, message: string): void {
    const time = new Date().toLocaleTimeString('en-GB', { hour12: false });
    const item = document.createElement('li');
    item.className = `log-line log-${level}`;
    const stamp = document.createElement('span');
    stamp.className = 'log-time';
    stamp.textContent = time;
    const mark = document.createElement('span');
    mark.className = 'log-mark';
    mark.textContent = MARKS[level];
    const text = document.createElement('span');
    text.className = 'log-text';
    // textContent, never innerHTML: a filename is user-controlled and this is
    // the one place a filename is rendered.
    text.textContent = message;
    item.append(stamp, mark, text);
    this.list.appendChild(item);
    this.list.scrollTop = this.list.scrollHeight;
    if (this.root.hidden) {
      this.unread++;
      this.renderBadge();
    }
  }

  private renderBadge(): void {
    this.badge.textContent = this.unread > 0 ? String(this.unread) : '';
    this.badge.hidden = this.unread === 0;
  }

  private async copy(): Promise<void> {
    const text = [...this.list.querySelectorAll('.log-line')]
      .map((line) => line.textContent?.replace(/\s+/g, ' ').trim() ?? '')
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      this.add('good', 'Event log copied to the clipboard.');
    } catch {
      this.add('warn', 'The browser refused clipboard access — select the lines and copy them by hand.');
    }
  }
}
